// Right-panel properties editor for the currently selected InteractivePlugin item.
// Renders kind-appropriate fields; all writes go to item.params via updateItemParam.
// Graded items (TrueFalse, YesNo) have "Doğruysa göster / Yanlışsa göster" pickers
// that reference sibling items — the referenced items are revealed/hidden at runtime.
import React from "react";
import i18n from "i18next";
import { connect } from "react-redux";
import { updateItemParam } from "../../../../common/actions";

/* eslint-disable react/prop-types */

const ALIGN_OPTIONS = [
  { value: "left", label: i18n.t("ItemProps.AlignLeft") || "Sol" },
  { value: "center", label: i18n.t("ItemProps.AlignCenter") || "Orta" },
  { value: "right", label: i18n.t("ItemProps.AlignRight") || "Sağ" },
];

const KIND_LABELS = {
  VisualLabel: i18n.t("ItemProps.Kind.VisualLabel") || "Görsel Etiket",
  VisualImage: i18n.t("ItemProps.Kind.VisualImage") || "Görsel",
  TrueFalse: i18n.t("ItemProps.Kind.TrueFalse") || "Doğru / Yanlış",
  YesNo: i18n.t("ItemProps.Kind.YesNo") || "Evet / Hayır",
  FlipCard: i18n.t("ItemProps.Kind.FlipCard") || "Flip Kart",
  MCQ: i18n.t("ItemProps.Kind.MCQ") || "Çoktan Seçmeli",
  MultipleChoice: i18n.t("ItemProps.Kind.MCQ") || "Çoktan Seçmeli",
  MultipleAnswer: i18n.t("ItemProps.Kind.MultipleAnswer") || "Çok Cevaplı",
  FillInBlank: i18n.t("ItemProps.Kind.FillInBlank") || "Boşluk Doldur",
  DragDrop: i18n.t("ItemProps.Kind.DragDrop") || "Sürükle Bırak",
  Matching: i18n.t("ItemProps.Kind.Matching") || "Eşleştirme",
  Ordering: i18n.t("ItemProps.Kind.Ordering") || "Sıralama",
  BranchingQuestion: i18n.t("ItemProps.Kind.BranchingQuestion") || "Dallanma Sorusu",
  EndScreen: i18n.t("ItemProps.Kind.EndScreen") || "Bitiş Ekranı",
  DialogCards: i18n.t("ItemProps.Kind.DialogCards") || "Diyalog Kartları",
  MemoryGame: i18n.t("ItemProps.Kind.MemoryGame") || "Hafıza Oyunu",
  DragTheWords: i18n.t("ItemProps.Kind.DragTheWords") || "Kelime Sürükle",
  FindTheWords: i18n.t("ItemProps.Kind.FindTheWords") || "Kelime Bul",
  ImageHotspots: i18n.t("ItemProps.Kind.ImageHotspots") || "Sıcak Noktalar",
  QuestionSet: i18n.t("ItemProps.Kind.QuestionSet") || "Soru Seti",
  PersonalityQuiz: i18n.t("ItemProps.Kind.PersonalityQuiz") || "Kişilik Testi",
  CoursePresentation: i18n.t("ItemProps.Kind.CoursePresentation") || "Slayt Sunumu",
  SpeakTheWords: i18n.t("ItemProps.Kind.SpeakTheWords") || "Konuşarak Cevapla",
  ImageSequencing: i18n.t("ItemProps.Kind.ImageSequencing") || "Görsel Sırala",
  SingleChoiceSet: i18n.t("ItemProps.Kind.SingleChoiceSet") || "Tek Seçim Seti",
  DragQuestion: i18n.t("ItemProps.Kind.DragQuestion") || "Sürükle Soru",
  Essay: i18n.t("ItemProps.Kind.Essay") || "Açık Uçlu Yazı",
};

// Kinds that can be shown/hidden as a result of a graded answer
const DISPLAYABLE_KINDS = new Set(["VisualImage", "VisualLabel", "EndScreen", "FlipCard"]);

// Available action types for graded item outcomes
const OUTCOME_ACTION_KINDS = [
  { value: "showItem", label: i18n.t("ItemProps.Action.ShowItem") || "Elementi göster" },
  { value: "hideItem", label: i18n.t("ItemProps.Action.HideItem") || "Elementi gizle" },
  { value: "resume", label: i18n.t("ItemProps.Action.Resume") || "Devam et (timeline)" },
  { value: "complete", label: i18n.t("ItemProps.Action.Complete") || "İnteraktifi tamamla" },
];
const ITEM_SELECTOR_KINDS = new Set(["showItem", "hideItem"]);

// Read a param field, falling back to top-level item field
function pval(item, ...keys) {
  const p = item.params || {};
  for (const k of keys) {
    if (p[k] !== undefined) return p[k];
    if (item[k] !== undefined) return item[k];
  }
  return undefined;
}

function Field({ label, children }) {
  return (
    <label className="item-props__field">
      <span className="item-props__field-label">{label}</span>
      {children}
    </label>
  );
}

// Per-outcome action list editor (correct / wrong).
function OutcomeActionList({ outcomeLabel, actions, allItems, selfId, onChange }) {
  const displayable = (allItems || []).filter(
    (it) => it && it.id !== selfId && DISPLAYABLE_KINDS.has(it.kind)
  );

  const setAction = (idx, patch) => {
    const next = actions.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChange(next);
  };
  const removeAction = (idx) => onChange(actions.filter((_, i) => i !== idx));
  const addAction = () => onChange([...actions, { kind: "showItem", itemId: null }]);

  return (
    <div className="item-props__action-outcome">
      <div className="item-props__action-outcome-label">{outcomeLabel}</div>
      {actions.map((action, idx) => (
        <div key={idx} className="item-props__action-row">
          <select
            className="item-props__input item-props__action-kind"
            value={action.kind || "showItem"}
            onChange={(e) => {
              const newKind = e.target.value;
              setAction(idx, {
                kind: newKind,
                itemId: ITEM_SELECTOR_KINDS.has(newKind) ? (action.itemId ?? null) : undefined,
              });
            }}
          >
            {OUTCOME_ACTION_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          {ITEM_SELECTOR_KINDS.has(action.kind) && (
            <select
              className="item-props__input item-props__action-target"
              value={action.itemId ?? ""}
              onChange={(e) => setAction(idx, { itemId: e.target.value || null })}
            >
              <option value="">{i18n.t("ItemProps.SelectPlaceholder") || "— Seç —"}</option>
              {displayable.map((it) => {
                const p = it.params || {};
                const preview =
                  p.text ?? it.text ?? p.imageUrl ?? it.imageUrl ?? p.title ?? it.title ?? it.kind;
                const kindLbl = KIND_LABELS[it.kind] || it.kind;
                return (
                  <option key={it.id} value={it.id}>
                    {kindLbl}: {String(preview).slice(0, 28) || it.id}
                  </option>
                );
              })}
            </select>
          )}
          <button type="button" className="item-props__opt-del" onClick={() => removeAction(idx)}>
            ×
          </button>
        </div>
      ))}
      <button type="button" className="item-props__add-opt" onClick={addAction}>
        {i18n.t("ItemProps.AddAction") || "+ Eylem Ekle"}
      </button>
    </div>
  );
}

// Shared outcome-actions section appended to graded items (TrueFalse, YesNo, MCQ).
function GradedActionsSection({ item, allItems, set }) {
  const correctActions = (item.params && item.params.onCorrectActions) || [];
  const wrongActions = (item.params && item.params.onWrongActions) || [];
  return (
    <>
      <div className="item-props__divider" />
      <div className="item-props__section-label">
        {i18n.t("ItemProps.AnswerActions") || "Cevap Eylemleri"}
      </div>
      <OutcomeActionList
        outcomeLabel={i18n.t("ItemProps.OnCorrect") || "✓ Doğruysa"}
        actions={correctActions}
        allItems={allItems}
        selfId={item.id}
        onChange={(next) => set("onCorrectActions", next)}
      />
      <OutcomeActionList
        outcomeLabel={i18n.t("ItemProps.OnWrong") || "✗ Yanlışsa"}
        actions={wrongActions}
        allItems={allItems}
        selfId={item.id}
        onChange={(next) => set("onWrongActions", next)}
      />
    </>
  );
}

// onClick action section for visual (non-graded) items.
function ClickActionsSection({ item, allItems, set }) {
  const clickActions = (item.params && item.params.onClickActions) || [];
  return (
    <>
      <div className="item-props__divider" />
      <div className="item-props__section-label">
        {i18n.t("ItemProps.ClickActions") || "Tıklama Eylemleri"}
      </div>
      <OutcomeActionList
        outcomeLabel={i18n.t("ItemProps.OnClick") || "👆 Tıklanınca"}
        actions={clickActions}
        allItems={allItems}
        selfId={item.id}
        onChange={(next) => set("onClickActions", next)}
      />
    </>
  );
}

function ItemPropertiesPanel({ boxId, item, allItems, onChangeParam }) {
  if (!item) return null;
  const { kind } = item;
  const set = (field, value) => onChangeParam(boxId, item.id, ["params", field], value);

  const kindLabel = KIND_LABELS[kind] || kind;

  const promptField = (label = i18n.t("ItemProps.PromptDefault") || "Soru / Açıklama") => (
    <Field label={label}>
      <textarea
        className="item-props__input item-props__textarea"
        value={pval(item, "prompt") ?? ""}
        onChange={(e) => set("prompt", e.target.value)}
        rows={2}
      />
    </Field>
  );

  let fields = null;

  switch (kind) {
    case "VisualLabel":
      fields = (
        <>
          <Field label={i18n.t("ItemProps.Text") || "Metin"}>
            <textarea
              className="item-props__input item-props__textarea"
              value={pval(item, "text") ?? ""}
              onChange={(e) => set("text", e.target.value)}
              rows={2}
            />
          </Field>
          <div className="item-props__row">
            <Field label={i18n.t("ItemProps.FontSize") || "Yazı Boyutu"}>
              <input
                type="number"
                className="item-props__input"
                value={pval(item, "fontSize") ?? 18}
                min={8}
                max={72}
                onChange={(e) => set("fontSize", Number(e.target.value))}
              />
            </Field>
            <Field label={i18n.t("ItemProps.Color") || "Renk"}>
              <input
                type="color"
                className="item-props__color"
                value={pval(item, "color") ?? "#1e293b"}
                onChange={(e) => set("color", e.target.value)}
              />
            </Field>
          </div>
          <Field label={i18n.t("ItemProps.Alignment") || "Hizalama"}>
            <select
              className="item-props__input"
              value={pval(item, "align") ?? "center"}
              onChange={(e) => set("align", e.target.value)}
            >
              {ALIGN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <label className="item-props__check">
            <input
              type="checkbox"
              checked={!!(pval(item, "bold") ?? false)}
              onChange={(e) => set("bold", e.target.checked)}
            />
            <span>{i18n.t("ItemProps.Bold") || "Kalın (Bold)"}</span>
          </label>
          <ClickActionsSection item={item} allItems={allItems} set={set} />
        </>
      );
      break;

    case "VisualImage":
      fields = (
        <>
          <Field label={i18n.t("ItemProps.ImageUrl") || "Görsel URL"}>
            <input
              type="url"
              className="item-props__input"
              value={pval(item, "imageUrl") ?? ""}
              placeholder="https://..."
              onChange={(e) => set("imageUrl", e.target.value)}
            />
          </Field>
          <Field label={i18n.t("ItemProps.ObjectFit") || "Sığdırma (Fit)"}>
            <select
              className="item-props__input"
              value={pval(item, "objectFit") ?? "contain"}
              onChange={(e) => set("objectFit", e.target.value)}
            >
              <option value="contain">Contain</option>
              <option value="cover">Cover</option>
              <option value="fill">Fill</option>
            </select>
          </Field>
          <Field label={i18n.t("ItemProps.AltText") || "Alt Metin"}>
            <input
              type="text"
              className="item-props__input"
              value={pval(item, "alt") ?? ""}
              placeholder={i18n.t("ItemProps.AltTextPlaceholder") || "Erişilebilirlik açıklaması"}
              onChange={(e) => set("alt", e.target.value)}
            />
          </Field>
          <ClickActionsSection item={item} allItems={allItems} set={set} />
        </>
      );
      break;

    case "TrueFalse":
      fields = (
        <>
          {promptField(i18n.t("ItemProps.Question") || "Soru")}
          <Field label={i18n.t("ItemProps.CorrectAnswer") || "Doğru Cevap"}>
            <select
              className="item-props__input"
              value={String(pval(item, "correctAnswer") ?? "true")}
              onChange={(e) => set("correctAnswer", e.target.value === "true")}
            >
              <option value="true">{i18n.t("ItemProps.True") || "Doğru"}</option>
              <option value="false">{i18n.t("ItemProps.False") || "Yanlış"}</option>
            </select>
          </Field>
          <div className="item-props__row">
            <Field label={i18n.t("ItemProps.TrueLabel") || "'Doğru' Etiketi"}>
              <input
                type="text"
                className="item-props__input"
                value={pval(item, "trueLabel") ?? "True"}
                onChange={(e) => set("trueLabel", e.target.value)}
              />
            </Field>
            <Field label={i18n.t("ItemProps.FalseLabel") || "'Yanlış' Etiketi"}>
              <input
                type="text"
                className="item-props__input"
                value={pval(item, "falseLabel") ?? "False"}
                onChange={(e) => set("falseLabel", e.target.value)}
              />
            </Field>
          </div>
          <GradedActionsSection item={item} allItems={allItems} set={set} />
        </>
      );
      break;

    case "YesNo":
      fields = (
        <>
          {promptField(i18n.t("ItemProps.Question") || "Soru")}
          <Field label={i18n.t("ItemProps.CorrectAnswer") || "Doğru Cevap"}>
            <select
              className="item-props__input"
              value={String(pval(item, "correctAnswer") ?? "true")}
              onChange={(e) => set("correctAnswer", e.target.value === "true")}
            >
              <option value="true">{i18n.t("ItemProps.Yes") || "Evet"}</option>
              <option value="false">{i18n.t("ItemProps.No") || "Hayır"}</option>
            </select>
          </Field>
          <div className="item-props__row">
            <Field label={i18n.t("ItemProps.YesLabel") || "'Evet' Etiketi"}>
              <input
                type="text"
                className="item-props__input"
                value={pval(item, "yesLabel") ?? "Yes"}
                onChange={(e) => set("yesLabel", e.target.value)}
              />
            </Field>
            <Field label={i18n.t("ItemProps.NoLabel") || "'Hayır' Etiketi"}>
              <input
                type="text"
                className="item-props__input"
                value={pval(item, "noLabel") ?? "No"}
                onChange={(e) => set("noLabel", e.target.value)}
              />
            </Field>
          </div>
          <GradedActionsSection item={item} allItems={allItems} set={set} />
        </>
      );
      break;

    case "MCQ":
    case "MultipleChoice": {
      const options = pval(item, "options") ?? [];
      const correctIndex = pval(item, "correctIndex") ?? 0;
      fields = (
        <>
          {promptField(i18n.t("ItemProps.Question") || "Soru")}
          <div className="item-props__label-row">
            <span className="item-props__field-label">
              {i18n.t("ItemProps.Options") || "Seçenekler"}
            </span>
            <button
              type="button"
              className="item-props__add-opt"
              onClick={() => set("options", [...options, ""])}
            >
              {i18n.t("ItemProps.Add") || "+ Ekle"}
            </button>
          </div>
          {options.map((opt, idx) => (
            <div key={idx} className="item-props__opt-row">
              <input
                type="radio"
                className="item-props__opt-radio"
                checked={correctIndex === idx}
                onChange={() => set("correctIndex", idx)}
                title={i18n.t("ItemProps.CorrectAnswerTitle") || "Doğru cevap"}
              />
              <input
                type="text"
                className="item-props__input item-props__opt-text"
                value={opt}
                onChange={(e) => {
                  const next = [...options];
                  next[idx] = e.target.value;
                  set("options", next);
                }}
              />
              <button
                type="button"
                className="item-props__opt-del"
                onClick={() => {
                  const next = options.filter((_, i) => i !== idx);
                  set("options", next);
                  if (correctIndex >= next.length) set("correctIndex", 0);
                }}
              >
                ×
              </button>
            </div>
          ))}
          <GradedActionsSection item={item} allItems={allItems} set={set} />
        </>
      );
      break;
    }

    case "FillInBlank": {
      const acceptedAnswers = pval(item, "acceptedAnswers") ?? [""];
      fields = (
        <>
          {promptField(i18n.t("ItemProps.Question") || "Soru")}
          <Field
            label={i18n.t("ItemProps.AcceptedAnswers") || "Kabul Edilen Cevaplar (virgülle ayırın)"}
          >
            <input
              type="text"
              className="item-props__input"
              value={acceptedAnswers.join(", ")}
              onChange={(e) =>
                set(
                  "acceptedAnswers",
                  e.target.value.split(",").map((s) => s.trim())
                )
              }
            />
          </Field>
          <label className="item-props__check">
            <input
              type="checkbox"
              checked={!!(pval(item, "caseSensitive") ?? false)}
              onChange={(e) => set("caseSensitive", e.target.checked)}
            />
            <span>{i18n.t("ItemProps.CaseSensitive") || "Büyük/küçük harf duyarlı"}</span>
          </label>
        </>
      );
      break;
    }

    case "FlipCard":
      fields = (
        <>
          <Field label={i18n.t("ItemProps.FrontFace") || "Ön Yüz"}>
            <textarea
              className="item-props__input item-props__textarea"
              value={pval(item, "front") ?? ""}
              rows={2}
              onChange={(e) => set("front", e.target.value)}
            />
          </Field>
          <Field label={i18n.t("ItemProps.BackFace") || "Arka Yüz"}>
            <textarea
              className="item-props__input item-props__textarea"
              value={pval(item, "back") ?? ""}
              rows={2}
              onChange={(e) => set("back", e.target.value)}
            />
          </Field>
        </>
      );
      break;

    case "EndScreen":
      fields = (
        <>
          <Field label={i18n.t("ItemProps.Title") || "Başlık"}>
            <input
              type="text"
              className="item-props__input"
              value={pval(item, "title") ?? ""}
              onChange={(e) => set("title", e.target.value)}
            />
          </Field>
          <Field label={i18n.t("ItemProps.Subtitle") || "Alt Başlık"}>
            <input
              type="text"
              className="item-props__input"
              value={pval(item, "subtitle") ?? ""}
              onChange={(e) => set("subtitle", e.target.value)}
            />
          </Field>
          <label className="item-props__check">
            <input
              type="checkbox"
              checked={!!(pval(item, "showScore") ?? true)}
              onChange={(e) => set("showScore", e.target.checked)}
            />
            <span>{i18n.t("ItemProps.ShowScore") || "Skoru göster"}</span>
          </label>
        </>
      );
      break;

    case "Essay":
      fields = (
        <>
          {promptField(i18n.t("ItemProps.QuestionInstruction") || "Soru / Yönerge")}
          <Field label={i18n.t("ItemProps.PlaceholderText") || "Placeholder Metin"}>
            <input
              type="text"
              className="item-props__input"
              value={pval(item, "placeholder") ?? ""}
              onChange={(e) => set("placeholder", e.target.value)}
            />
          </Field>
          <Field label={i18n.t("ItemProps.MinWordCount") || "Min. Kelime Sayısı"}>
            <input
              type="number"
              className="item-props__input"
              value={pval(item, "minWords") ?? 20}
              min={0}
              onChange={(e) => set("minWords", Number(e.target.value))}
            />
          </Field>
        </>
      );
      break;

    default:
      fields = (
        <>
          {promptField()}
          <p className="item-props__complex-note">
            {i18n.t("ItemProps.ComplexNote") ||
              "Bu element türü için ek ayarlar Workflow sekmesinden yönetilir."}
          </p>
        </>
      );
  }

  return (
    <div className="item-props">
      <div className="item-props__head">
        <span className="item-props__kind-badge">{kindLabel}</span>
        <span className="item-props__id">{item.id}</span>
      </div>
      <div className="item-props__fields">{fields}</div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const present = (state.undoGroup && state.undoGroup.present) || {};
  const pluginToolbar =
    (present.pluginToolbarsById && present.pluginToolbarsById[ownProps.boxId]) || {};
  const allItems = (pluginToolbar.state && pluginToolbar.state.items) || [];
  const selectedItemId =
    state.interactivityStudio && state.interactivityStudio.selectedBoxId === ownProps.boxId
      ? state.interactivityStudio.selectedItemId
      : null;
  const item = selectedItemId
    ? allItems.find((it) => it && it.id === selectedItemId) || null
    : null;
  return { item, allItems };
};

const mapDispatchToProps = (dispatch) => ({
  onChangeParam: (boxId, itemId, path, value) =>
    dispatch(updateItemParam(boxId, itemId, path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemPropertiesPanel);
